import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_BASE_URL } from '../../shared/api/api.tokens';
import { REQUIRE_AUTH } from '../../shared/interceptors/auth-context';

export interface BusinessScheduleRange {
  businessScheduleId: string;
  dayOfWeek: number; // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  startTime: string; // Format: HH:mm
  endTime: string; // Format: HH:mm
  isActive: boolean;
}

export interface ScheduleBlock {
  scheduleBlockId: string;
  businessId: string;
  professionalId?: string | null; // null: applies to the whole business
  agendaId?: string | null; // Legacy alias of professionalId
  startDate: string;
  endDate: string;
  startTime: string; // Format: HH:mm
  endTime: string; // Format: HH:mm
  title?: string | null;
  description?: string | null;
}

export interface GetBusinessScheduleResponse {
  businessId: string;
  timeRanges: BusinessScheduleRange[];
}

export interface GetScheduleBlocksResponse {
  blocks: ScheduleBlock[];
}

export interface CreateBusinessScheduleRequest {
  businessId: string;
  timeRanges: Array<{
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    isActive?: boolean;
  }>;
}

export interface CreateScheduleBlockRequest {
  businessId: string;
  professionalId?: string | null; // null: applies to the whole business
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  title?: string | null;
  description?: string | null;
}

@Injectable({ providedIn: 'root' })
export class ScheduleService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(API_BASE_URL);

  getBusinessSchedule(businessId: string): Observable<GetBusinessScheduleResponse> {
    return this.http.get<GetBusinessScheduleResponse>(
      `${this.baseUrl}/api/v1/business-schedules/${businessId}`,
      {
        context: new HttpContext().set(REQUIRE_AUTH, true),
      }
    );
  }

  createBusinessSchedule(request: CreateBusinessScheduleRequest): Observable<{ businessId: string; rangesCreated: number }> {
    return this.http.post<{ businessId: string; rangesCreated: number }>(
      `${this.baseUrl}/api/v1/business-schedules`,
      request,
      {
        context: new HttpContext().set(REQUIRE_AUTH, true),
      }
    );
  }

  getScheduleBlocks(
    businessId: string,
    professionalId?: string | null,
    startDate?: string,
    endDate?: string
  ): Observable<GetScheduleBlocksResponse> {
    let params = new HttpParams().set('businessId', businessId);
    if (professionalId) params = params.set('professionalId', professionalId);
    if (startDate) params = params.set('startDate', startDate);
    if (endDate) params = params.set('endDate', endDate);

    return this.http.get<GetScheduleBlocksResponse>(
      `${this.baseUrl}/api/v1/schedule-blocks`,
      {
        params,
        context: new HttpContext().set(REQUIRE_AUTH, true),
      }
    );
  }

  createScheduleBlock(request: CreateScheduleBlockRequest): Observable<ScheduleBlock> {
    return this.http.post<ScheduleBlock>(
      `${this.baseUrl}/api/v1/schedule-blocks`,
      request,
      {
        context: new HttpContext().set(REQUIRE_AUTH, true),
      }
    );
  }

  deleteScheduleBlock(scheduleBlockId: string, businessId: string): Observable<{ scheduleBlockId: string; success: boolean }> {
    return this.http.delete<{ scheduleBlockId: string; success: boolean }>(
      `${this.baseUrl}/api/v1/schedule-blocks/${scheduleBlockId}`,
      {
        params: new HttpParams().set('businessId', businessId),
        context: new HttpContext().set(REQUIRE_AUTH, true),
      }
    );
  }
}

